/**
 * FortiGate fleet overview: one page that sums up every device the backend
 * knows about (GET /fleet.json, see dashboard.build_fleet_document).
 *
 * FortinetDashboardComponent shows it when "All devices" is picked in the
 * device picker, but I can also use it on its own:
 *
 *   <app-fortinet-fleet [range]="'24h'" (openDevice)="goTo($event)" />
 *
 * The helpers shared with the device page are in fortinet-shared.ts.
 */
import { CommonModule } from '@angular/common';
import { HttpClient, HttpParams } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Injectable,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { EMPTY, Observable, Subject, merge } from 'rxjs';
import { catchError, switchMap, takeUntil, tap } from 'rxjs/operators';
import type { EChartsOption, SeriesOption } from 'echarts';
import { environment } from '../../environments/environment';

import {
  AckFields, AckNoteEditor, Badge, ChartKit, EVENT_KIND_LABEL, EventKind,
  FORTINET_USER, FortiEvent, Formatter, ackNoteLine,
  eventBadge, eventKindsOf, eventWhen, firstSeenText, firstSeenTitle,
  HistoryRange, SEV_WORD, Sev, Thresholds, Values, ackNote, describeApiError,
  Level, daysLevelOf, downloadCsv, evidenceOf, fmtAge, fmtBps, fmtBytes, fmtNum, fmtPct,
  fmtPctOrNum, fmtStamp, isPct, issueBadge, levelCls, levelOf, pollTriggers,
  SortValue, TableSort, issuesTabFromKey, sortAckedIssues, sortOpenIssues, sparkPath, thresholdsOf,
  worstSevClass, compareGroupsAsTree, deviceIsInGroup, groupDepth, groupLeafName,
  groupPathText, groupsOfDevice, groupsWithParents, mainGroupOf,
} from './fortinet-shared';

/* ============================================================================
 * Document shape (GET /fleet.json)
 * ========================================================================== */

export type DeviceState = 'ok' | 'warning' | 'critical' | 'unreachable' | 'offline';
export type FirmwareStatus = 'on-target' | 'behind' | 'ahead';

export interface FleetDevice {
  serial: string;
  host: string | null;
  label: string;
  /** own hostname, when the HA member list reports it */
  hostname?: string | null;
  /** config.yaml `groups:`, main group first (absent from older backends:
   *  read it with groupsOfDevice()) */
  groups?: string[];
  /** the single group sent by older backends */
  group?: string | null;
  version: string | null;
  firmware_latest: string | null;
  firmware_target?: string | null;
  firmware_status?: FirmwareStatus | null;
  upgrade: boolean;
  state: DeviceState;
  last_poll: number | null;      // epoch ms
  poll_age: number | null;       // seconds
  interval: number | null;       // seconds
  poll_ok_pct: number | null;
  throughput_bps: number | null;
  prev_throughput_bps: number | null;
  rx_bps: number | null;
  tx_bps: number | null;
  sessions: number | null;
  setup_rate: number | null;
  cpu: number | null;
  mem: number | null;
  ifaces_up: number | null;
  ifaces_total: number | null;
  tunnels_up: number;
  tunnels_total: number;
  sdwan_up: number;
  sdwan_total: number;
  ssl_users: number | null;
  ha_role: 'primary' | 'secondary' | 'alone' | 'standalone';
  ha_members: number;
  admins: number;
  /** open issues only: acknowledged ones are not counted */
  issues: Record<Sev, number>;
  /** acknowledged issues still detected (absent from older backends) */
  issues_acked?: number;
  /** the same, by severity (absent from older backends) */
  issues_acked_sev?: Record<Sev, number>;
  top_issue: { sev: Sev; title: string; detail: string } | null;
  license_min_days: number | null;
  license_min_name: string | null;
  policies_total?: number;
  policies_unused?: number;
  policies_oversize?: number;
}

export interface FleetSeries {
  name: string;
  serial: string;
  t: number[];
  values: Values;
  min?: Values;
  max?: Values;
}

export interface FleetIssue extends AckFields {
  sev: Sev;
  title: string;
  detail: string;
  serial: string;
  device: string;
}

export interface FleetLicense {
  serial: string;
  device: string;
  name: string;
  status: string;
  days_left: number | null;
  expired: boolean;
  /** absent on older backends, which only list licenses */
  kind?: 'license' | 'certificate';
}

export interface FleetEvent extends FortiEvent {
  serial: string;
  device: string;
}

export interface FleetPolicyHygiene {
  serial: string;
  device: string;
  total: number;
  unused: string[];
  unused_count: number;
  oversize: string[];
  oversize_count: number;
}

export interface FleetSdwanCell {
  status: string | null;
  latency: number | null;
  jitter: number | null;
  loss: number | null;
}

export interface FleetSdwan {
  members: string[];
  devices: { serial: string; device: string; cells: Record<string, FleetSdwanCell> }[];
}

export interface FleetDocument {
  meta: {
    serials: string[];
    history_range: string | null;
    history_ranges: HistoryRange[];
    generated_at: number;
    bucketed: boolean;
    thresholds?: Partial<Thresholds>;
    groups?: string[];
  };
  totals: {
    devices: number;
    online: number;
    offline: number;
    throughput_bps: number | null;
    prev_throughput_bps: number | null;
    sessions: number | null;
    setup_rate: number | null;
    ssl_users: number | null;
    tunnels_up: number;
    tunnels_total: number;
    sdwan_up: number;
    sdwan_total: number;
    issues: Record<Sev, number>;
    issues_acked?: number;
    licenses_expired: number;
    licenses_expiring: number;
    certificates_expired?: number;
    certificates_expiring?: number;
    upgrades: number;
    versions: { name: string; value: number }[];
    firmware_targeted?: number;
    firmware_on_target?: number;
    firmware_behind?: number;
    policies_unused?: number;
    policies_oversize?: number;
  };
  devices: FleetDevice[];
  throughput: FleetSeries[];
  sessions: FleetSeries[];
  cpu: FleetSeries[];
  memory: FleetSeries[];
  daily: { days: string[]; series: { name: string; serial: string; values: number[] }[] };
  issues: FleetIssue[];
  issues_total: number;
  /** absent from older backends, which cannot acknowledge issues */
  issues_acked?: FleetIssue[];
  issues_acked_total?: number;
  licenses: FleetLicense[];
  /** newest first across devices (absent from older backends) */
  events?: FleetEvent[];
  events_total?: number;
  /** absent from older backends */
  policy_hygiene?: FleetPolicyHygiene[];
  sdwan?: FleetSdwan;
}

/* ============================================================================
 * Service
 * ========================================================================== */

@Injectable({ providedIn: 'root' })
export class FortinetFleetService {
  constructor(private http: HttpClient) {}

  /** Same base URL rule as FortinetDashboardService.getDocument. */
  getFleet(range?: string | null, apiBase = environment.fortinetApiUrl): Observable<FleetDocument> {
    let params = new HttpParams();
    if (range) {
      params = params.set('range', range);
    }
    return this.http.get<FleetDocument>(`${apiBase}/fleet.json`, { params });
  }

  /** Acknowledges an issue (acked=true) or brings it back (acked=false).
   *  Same base URL as getFleet. */
  setIssueAck(serial: string, issue: FleetIssue, acked: boolean,
              apiBase = environment.fortinetApiUrl, extra?: { note?: string; by?: string }): Observable<unknown> {
    const body = {
      serial, acked,
      issue: { id: issue.id, sev: issue.sev, title: issue.title,
               detail: issue.detail },
      ...(extra ?? {}),
    };
    return this.http.post(`${apiBase}/issues/ack`, body);
  }
}

/* ============================================================================
 * View models
 * ========================================================================== */

interface KpiVm {
  label: string;
  value: string;
  suffix?: string;
  sub?: string;
  badge?: Badge | null;
}

interface RowVm {
  device: FleetDevice;
  /** lower-cased text the search box matches against */
  haystack: string;
  /** the groups of the device written for people ("France › Paris"), main group first */
  groupTexts: string[];
  attention: boolean;
  offline: boolean;
  state: Badge;
  /** small pill next to a "healthy" state: there are still issues, but
   *  none needs attention (acknowledged ones and notes) */
  stateMore: { text: string; title: string; cls: string } | null;
  acked: number;
  /** badge class of the most severe acknowledged issue */
  ackedCls: string;
  ackedTitle: string;
  firmware: Badge | null;
  firmwareTitle: string;
  age: string;
  ageTitle: string;
  pollCls: string;
  throughput: string;
  /** SVG path of the throughput trend, '' without data */
  spark: string;
  sparkTitle: string;
  /** highest point of the trend, what the Trend column sorts on */
  sparkPeak: number | null;
  sessions: string;
  cpu: string;
  cpuCls: string;
  mem: string;
  memCls: string;
  ifaces: string;
  ifacesCls: string;
  tunnels: string;
  tunnelsCls: string;
  sdwan: string;
  sdwanCls: string;
  ha: Badge | null;
  license: string;
  licenseCls: string;
  licenseTitle: string;
}

interface GroupVm {
  /** the full path ("France/Paris"), '' for the devices without a group */
  name: string;
  /** the last level ("Paris"), or "No group" */
  label: string;
  /** the full path written for people ("France › Paris"), for the tooltip */
  pathText: string;
  /** 0 for a top group, 1 for a group inside it... (to indent it) */
  depth: number;
  /** devices in this group or in a group below it */
  devices: number;
  reporting: number;
  attention: number;
  throughput: number | null;
  sessions: number | null;
  issues: number;
}

/** One segment of the state strip above the device table. */
interface StateSegVm {
  state: DeviceState;
  label: string;
  count: number;
  /** share of the fleet, in % */
  pct: number;
}

interface SdwanCellVm {
  text: string;
  cls: string;
  title: string;
}

interface SdwanRowVm {
  serial: string;
  device: string;
  cells: SdwanCellVm[];
  /** what each member column sorts on, by sort key ("member:wan1") */
  sortValues: Record<string, number | null>;
}

export type StateFilter = 'all' | 'attention' | 'offline';

/** What filtersChanged reports. */
export interface FleetFilters {
  /** '' for the devices without a group, null for every group */
  groupFilter: string | null;
  /** any filter is on (search, state or group) */
  filtered: boolean;
  /** devices left in the table */
  shown: number;
}
export type SdwanMetric = 'loss' | 'latency' | 'jitter';

const STATE_RANK: Record<DeviceState, number> = {
  offline: 0, unreachable: 1, critical: 2, warning: 3, ok: 4,
};

const STATE_BADGE: Record<DeviceState, Badge> = {
  offline: { cls: 'label-danger', text: 'offline' },
  unreachable: { cls: 'label-danger', text: 'unreachable' },
  critical: { cls: 'label-danger', text: 'needs action' },
  warning: { cls: 'label-warning', text: 'check' },
  ok: { cls: 'label-success', text: 'healthy' },
};

/** HA column order: a cluster that lost its peer first. */
const HA_RANK: Record<FleetDevice['ha_role'], number> = {
  alone: 0, primary: 1, secondary: 2, standalone: 3,
};

/* ============================================================================
 * Table sorting: the value of a row for each column (see TableSort in
 * fortinet-shared.ts). The key is the one written in the template, in
 * sortTable(..., 'key'). null means "nothing to sort on": the row goes to
 * the bottom.
 * ========================================================================== */

/** "3 / 4 up" as a share between 0 and 1, so 1 / 2 comes before 3 / 4.
 *  null when the device has none. */
function shareUp(up: number | null, total: number | null): number | null {
  if (!total) return null;
  return (up ?? 0) / total;
}

function deviceSortValue(row: RowVm, key: string): SortValue {
  const device = row.device;
  switch (key) {
    case 'label': return device.label;
    case 'group': return mainGroupOf(groupsOfDevice(device));
    // offline first, healthy last
    case 'state': return STATE_RANK[device.state] ?? 9;
    // "7.4.10" after "7.4.9": the text compare reads the digits as numbers
    case 'version': return device.version;
    case 'poll_age': return device.poll_age;
    case 'throughput_bps': return device.throughput_bps;
    case 'trend': return row.sparkPeak;
    case 'sessions': return device.sessions;
    case 'cpu': return device.cpu;
    case 'mem': return device.mem;
    case 'ifaces': return shareUp(device.ifaces_up, device.ifaces_total);
    case 'tunnels': return shareUp(device.tunnels_up, device.tunnels_total);
    case 'sdwan': return shareUp(device.sdwan_up, device.sdwan_total);
    case 'ha': return HA_RANK[device.ha_role];
    case 'issues': {
      // One critical issue weighs more than any number of warnings, and
      // one warning more than any number of notes: each level gets its
      // own "digits" in the number.
      return device.issues.danger * 1e4 + device.issues.warning * 100
        + device.issues.info;
    }
    case 'license_min_days': return device.license_min_days;
    default: return null;
  }
}

function groupSortValue(group: GroupVm, key: string): SortValue {
  switch (key) {
    case 'label': return group.name;
    case 'devices': return group.devices;
    case 'reporting': return group.reporting;
    case 'attention': return group.attention;
    case 'issues': return group.issues;
    case 'throughput': return group.throughput;
    case 'sessions': return group.sessions;
    default: return null;
  }
}

function fleetLicenseSortValue(license: FleetLicense, key: string): SortValue {
  switch (key) {
    case 'device': return license.device;
    case 'name': return license.name;
    case 'expires': {
      if (license.days_left != null) return license.days_left;
      // expired but without a date: before everything that still has days
      return license.expired ? -1 : null;
    }
    default: return null;
  }
}

function hygieneSortValue(entry: FleetPolicyHygiene, key: string): SortValue {
  switch (key) {
    case 'device': return entry.device;
    case 'total': return entry.total;
    case 'unused': return entry.unused_count;
    case 'oversize': return entry.oversize_count;
    // the Candidates column lists the policy names: I sort on how many
    case 'candidates': return entry.unused_count + entry.oversize_count;
    default: return null;
  }
}

function sdwanRowSortValue(row: SdwanRowVm, key: string): SortValue {
  if (key === 'device') {
    return row.device;
  }
  // a member column, "member:wan1"
  return row.sortValues[key] ?? null;
}

/** Events listed in the merged timeline. */
const FLEET_EVENT_ROWS = 60;
/** Devices drawn individually on each comparison chart. */
const CHART_MAX_DEVICES = 8;
/** Sparkline box, in px. */
const SPARK_W = 72;
const SPARK_H = 18;

/* ============================================================================
 * Component
 * ========================================================================== */

@Component({
  selector: 'app-fortinet-fleet',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './fortinet-fleet.component.html',
  // The dashboard stylesheet brings the Clarity look (cards, tables,
  // labels, KPI cards), the fleet one only adds what is specific here.
  styleUrls: ['./fortinet-dashboard.component.css',
              './fortinet-fleet.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FortinetFleetComponent implements OnInit, OnChanges, OnDestroy {
  /** History window id ('1h', '24h', ...). null lets the backend decide. */
  @Input() range: string | null = null;
  /** Browser auto-refresh interval in ms, 0 turns it off. */
  @Input() refreshMs = 60_000;
  /** Base URL of the Fortinet routes of the NetScope API */
  @Input() apiBase = environment.fortinetApiUrl;
  /** Pauses the automatic refresh (the host's Pause button). When it's
   *  turned off again, it fetches right away if the data is older than one
   *  interval. */
  @Input() paused = false;
  /** The group the page opens on (a full path like "France/Paris"), for
   *  example when a group of the device page header is clicked. Only read
   *  when the page is created; null shows every group. */
  @Input() initialGroupFilter: string | null = null;

  /** A device was picked (row, issue or license click): its serial. */
  @Output() openDevice = new EventEmitter<string>();
  /** "Show" on an issue: open the device on the evidence of that issue. */
  @Output() openIssue = new EventEmitter<{ serial: string; id: string }>();
  /** Every document received, so a host page can sync its device list
   *  and range buttons. */
  @Output() loaded = new EventEmitter<FleetDocument>();
  /** Status line for a host header: 'updated 10:42' or an error. */
  @Output() status = new EventEmitter<{ text: string; err: boolean }>();
  /** The filters changed the page (search, state, group), or a new
   *  document was filtered again: a host navigation can follow the group
   *  and the counts it shows. */
  @Output() filtersChanged = new EventEmitter<FleetFilters>();

  // ---- template-bound ----
  loadedOnce = false;
  kpis: KpiVm[] = [];
  rows: RowVm[] = [];
  rowsTotal = 0;
  groups: GroupVm[] = [];
  issues: FleetIssue[] = [];
  issuesTotal = 0;
  issuesAcked: FleetIssue[] = [];
  issuesAckedTotal = 0;
  /** false with an older backend: no checkboxes then */
  ackSupported = false;
  /** issue keys with a request in flight (their checkbox is disabled) */
  ackPending = new Set<string>();
  licenses: FleetLicense[] = [];
  /** certificates are listed next to the licenses (newer backends) */
  hasCertRows = false;
  events: FleetEvent[] = [];
  eventsMatching = 0;
  eventKinds: EventKind[] = [];
  eventKind: EventKind | 'all' = 'all';
  eventsSupported = false;
  readonly eventKindLabel = EVENT_KIND_LABEL;
  readonly noteEd: AckNoteEditor;
  hygiene: FleetPolicyHygiene[] = [];
  hygieneOpen = new Set<string>();
  hygieneSupported = false;
  sdwanMembers: string[] = [];
  sdwanRows: SdwanRowVm[] = [];
  sdwanMetric: SdwanMetric = 'loss';
  showSdwan = false;
  showHa = false;
  showGroups = false;
  showCompliance = false;
  // Column sort of each table. The numbers start with the biggest (the
  // busiest, the most issues), the rest with the worst or A to Z.
  readonly deviceSort = new TableSort('state', true,
    ['poll_age', 'throughput_bps', 'trend', 'sessions', 'cpu', 'mem', 'issues']);
  readonly groupSort = new TableSort(null, true,
    ['devices', 'reporting', 'attention', 'issues', 'throughput', 'sessions']);
  readonly licenseSort = new TableSort();
  readonly hygieneSort = new TableSort(null, true, ['total', 'unused', 'oversize', 'candidates']);
  /** its descendingFirst is filled in buildSdwan: every member column
   *  starts with the worst value (a member down, then the highest) */
  readonly sdwanSort = new TableSort();
  errorText = '';
  /** "busiest 8 of 14 shown", per comparison chart */
  chartNotes: Record<string, string> = {};

  /** Devices per state, whole fleet (never narrowed by the filters). */
  stateSegs: StateSegVm[] = [];

  // ---- filters (table, charts and lists; not the KPI figures) ----
  filterText = '';
  /** one exact state, picked on the state strip; null: any */
  exactState: DeviceState | null = null;
  stateFilter: StateFilter = 'all';
  /** null: every group; '' the devices without a group */
  groupFilter: string | null = null;
  readonly stateFilters: { id: StateFilter; label: string }[] = [
    { id: 'all', label: 'All' },
    { id: 'attention', label: 'Needs attention' },
    { id: 'offline', label: 'Not reporting' },
  ];

  private doc: FleetDocument | null = null;
  private allRows: RowVm[] = [];
  // The lists of the tables before their column sort (see
  // updateSortedTables), so a third click can go back to this order.
  private rowsInOrder: RowVm[] = [];
  private groupsInOrder: GroupVm[] = [];
  private licensesInOrder: FleetLicense[] = [];
  private hygieneInOrder: FleetPolicyHygiene[] = [];
  private sdwanRowsInOrder: SdwanRowVm[] = [];
  thresholds: Thresholds = thresholdsOf();
  private kit: ChartKit;
  private lastLoadedAt = 0;
  private readonly reload$ = new Subject<void>();
  private readonly destroyed$ = new Subject<void>();
  private destroyed = false;
  private themeObserver: MutationObserver | null = null;
  private resizeTimer: ReturnType<typeof setTimeout> | null = null;
  private stopWatchingWidth: (() => void) | null = null;

  constructor(
    private svc: FortinetFleetService,
    private hostRef: ElementRef<HTMLElement>,
    private zone: NgZone,
    private cdr: ChangeDetectorRef,
  ) {
    this.kit = new ChartKit(hostRef.nativeElement, zone, 0.12);
    this.noteEd = new AckNoteEditor(inject(FORTINET_USER, { optional: true }));
  }

  ngOnInit(): void {
    if (this.initialGroupFilter !== null) {
      this.groupFilter = this.initialGroupFilter;
    }
    const triggers$ = merge(
      pollTriggers(this.refreshMs, () => this.lastLoadedAt, () => this.paused),
      this.reload$,
    );
    triggers$
      .pipe(
        tap(() => {
          this.lastLoadedAt = Date.now();
        }),
        // switchMap drops a request still running when a new one starts
        switchMap(() => this.svc.getFleet(this.range, this.apiBase).pipe(
          catchError((error: unknown) => {
            this.onError(error);
            return EMPTY;
          }),
        )),
        takeUntil(this.destroyed$),
      )
      .subscribe((doc) => this.onDocument(doc));

    // redraw the charts when the light / dark theme changes
    this.themeObserver = new MutationObserver(() => {
      if (this.doc && !this.destroyed) {
        this.renderCharts();
      }
    });
    this.themeObserver.observe(document.body, {
      attributes: true, attributeFilter: ['cds-theme', 'class'],
    });

    // the charts follow the width of the page (see ChartKit.watchWidth)
    this.stopWatchingWidth = this.kit.watchWidth(this.hostRef.nativeElement, () => this.onResize());
  }

  ngOnChanges(changes: SimpleChanges): void {
    const pausedChange = changes['paused'];
    if (pausedChange && !pausedChange.firstChange && !pausedChange.currentValue
        && Date.now() - this.lastLoadedAt >= this.refreshMs) {
      // un-paused and the data is old: fetch now
      this.reload$.next();
    }

    const rangeChange = changes['range'];
    // A host that adopts the range this component got (null -> '24h' after
    // the first load) must not trigger a second request.
    if (rangeChange && !rangeChange.firstChange) {
      const currentRange = this.doc?.meta.history_range ?? rangeChange.previousValue;
      if (rangeChange.currentValue !== currentRange) {
        this.kit.forgetState('zoom');
        this.reload$.next();
      }
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.stopWatchingWidth?.();
    this.destroyed$.next();
    this.destroyed$.complete();
    this.reload$.complete();
    if (this.resizeTimer) {
      clearTimeout(this.resizeTimer);
    }
    this.themeObserver?.disconnect();
    this.kit.dispose();
  }

  /** Called by the width watcher started in ngOnInit (window resized, or the
   *  shell menu collapsed / opened). */
  onResize(): void {
    // wait until the resizing stops (120 ms) before redrawing
    if (this.resizeTimer) {
      clearTimeout(this.resizeTimer);
    }
    this.zone.runOutsideAngular(() => {
      this.resizeTimer = setTimeout(() => {
        this.resizeTimer = null;
        if (!this.destroyed) {
          this.kit.resize();
        }
      }, 120);
    });
  }

  /** Fetches now (the host's refresh button). */
  reload(): void {
    if (!this.destroyed) {
      this.reload$.next();
    }
  }

  // ---------------------------------------------------------------- actions
  open(serial: string): void {
    this.openDevice.emit(serial);
  }

  hasEvidence(issue: FleetIssue): boolean {
    return evidenceOf(issue.id) != null;
  }

  showIssue(issue: FleetIssue): void {
    if (issue.id) {
      this.openIssue.emit({ serial: issue.serial, id: issue.id });
    }
  }

  setFilterText(text: string): void {
    this.filterText = text;
    this.applyFilters();
  }

  setStateFilter(stateFilter: StateFilter): void {
    this.stateFilter = stateFilter;
    this.exactState = null;
    this.applyFilters();
  }

  /** A segment of the state strip: only that state (clicked again: all). */
  setExactState(state: DeviceState): void {
    this.exactState = this.exactState === state ? null : state;
    this.stateFilter = 'all';
    this.applyFilters();
  }

  /** The device table as shown (filters and order included). */
  exportDevicesCsv(): void {
    const header = [
      'device', 'serial', 'hostname', 'host', 'groups', 'state', 'fortios',
      'firmware_target', 'firmware_status', 'last_poll', 'poll_ok_pct',
      'throughput_bps', 'sessions', 'setup_rate', 'cpu', 'memory',
      'ifaces_up', 'ifaces_total', 'ipsec_up', 'ipsec_total', 'sdwan_up',
      'sdwan_total', 'ha_role', 'issues_critical', 'issues_warning',
      'issues_note', 'issues_acknowledged', 'license_min_days',
      'license_min_name',
    ];
    const lines: unknown[][] = [];
    for (const row of this.rows) {
      const device = row.device;
      // the groups in one cell, main group first: "France/Paris; Production"
      const groupsCell = groupsOfDevice(device).join('; ');
      lines.push([
        device.label, device.serial, device.hostname, device.host, groupsCell,
        device.state, device.version, device.firmware_target, device.firmware_status,
        device.last_poll ? fmtStamp(device.last_poll) : '', device.poll_ok_pct,
        device.throughput_bps, device.sessions, device.setup_rate, device.cpu, device.mem,
        device.ifaces_up, device.ifaces_total, device.tunnels_up, device.tunnels_total,
        device.sdwan_up, device.sdwan_total, device.ha_role, device.issues.danger,
        device.issues.warning, device.issues.info, device.issues_acked ?? 0,
        device.license_min_days, device.license_min_name,
      ]);
    }
    downloadCsv('fortigate-fleet-devices', header, lines);
  }

  setGroupFilter(group: string | null): void {
    this.groupFilter = group;
    this.applyFilters();
  }

  /** Spaces to put before a group name in the group list, to show it is
   *  inside another group (a <select> option can only hold text). */
  indentText(depth: number): string {
    // ' ' is a non-breaking space: the browser does not squeeze it
    return '   '.repeat(depth);
  }

  clearFilters(): void {
    this.filterText = '';
    this.stateFilter = 'all';
    this.exactState = null;
    this.groupFilter = null;
    this.applyFilters();
  }

  get filtered(): boolean {
    if (this.filterText.trim()) return true;
    if (this.stateFilter !== 'all') return true;
    if (this.exactState !== null) return true;
    return this.groupFilter !== null;
  }

  /** A click on a column title, in any table of the page. The first click
   *  sorts on the column, the second flips the direction, the third goes
   *  back to the default order (see TableSort). */
  sortTable(sort: TableSort, key: string): void {
    sort.toggle(key);
    this.updateSortedTables();
  }

  /** Sorts every table from its list in order. They are small, so I sort
   *  them all instead of finding which table the click was on. */
  private updateSortedTables(): void {
    this.rows = this.deviceSort.apply(this.rowsInOrder, deviceSortValue);
    this.groups = this.groupSort.apply(this.groupsInOrder, groupSortValue);
    this.licenses = this.licenseSort.apply(this.licensesInOrder, fleetLicenseSortValue);
    this.hygiene = this.hygieneSort.apply(this.hygieneInOrder, hygieneSortValue);
    this.sdwanRows = this.sdwanSort.apply(this.sdwanRowsInOrder, sdwanRowSortValue);
  }

  /** The sort key of a member column of the SD-WAN grid. */
  sdwanKey(member: string): string {
    return 'member:' + member;
  }

  issueBadge = issueBadge;
  ackNote = ackNote;
  ackNoteLine = ackNoteLine;
  firstSeenText = firstSeenText;
  firstSeenTitle = firstSeenTitle;
  eventBadge = eventBadge;
  eventWhen = eventWhen;
  fmtStamp = fmtStamp;
  fmtBps = fmtBps;
  fmtNum = fmtNum;

  /** Unique key of an issue across devices. */
  issueKey(issue: FleetIssue): string {
    return issue.serial + '|' + (issue.id ?? issue.sev + '|' + issue.title);
  }

  /** Which tab of the issues card is shown. */
  issuesTab: 'open' | 'acked' = 'open';
  /** Unique per instance, for the tab/panel ARIA ids. */
  readonly tabIdPrefix = 'issues-' + Math.random().toString(36).slice(2, 8);

  /** Tab click, or arrow keys on the tab list (the focus follows). */
  selectIssuesTab(tab: 'open' | 'acked', element?: HTMLElement): void {
    this.issuesTab = tab;
    element?.focus();
    this.cdr.markForCheck();
  }

  /** Arrows, Home and End on the issues tabs (see issuesTabFromKey). */
  onIssuesTabKey(event: KeyboardEvent, tabOpen: HTMLElement, tabAcked: HTMLElement): void {
    const nextTab = issuesTabFromKey(event.key, this.issuesTab);
    if (nextTab === null) {
      return;
    }
    event.preventDefault();
    if (nextTab === 'open') {
      this.selectIssuesTab('open', tabOpen);
    } else {
      this.selectIssuesTab('acked', tabAcked);
    }
  }

  setSdwanMetric(metric: SdwanMetric): void {
    this.sdwanMetric = metric;
    this.buildSdwan();
  }

  toggleHygiene(serial: string): void {
    // a new Set, so OnPush sees the change
    const next = new Set(this.hygieneOpen);
    if (next.has(serial)) {
      next.delete(serial);
    } else {
      next.add(serial);
    }
    this.hygieneOpen = next;
  }

  /** Checkbox on an issue: I move it between the open and acknowledged
   *  lists right away, then save it and reload, because the device states
   *  and counts are computed by the backend. If saving fails, the reload
   *  puts the issue back where the backend has it. */
  setAck(issue: FleetIssue, acked: boolean): void {
    const key = this.issueKey(issue);
    if (!issue.id || this.ackPending.has(key)) return;

    const pending = new Set(this.ackPending);
    pending.add(key);
    this.ackPending = pending;

    const isOtherIssue = (other: FleetIssue) => this.issueKey(other) !== key;
    if (acked) {
      this.issues = this.issues.filter(isOtherIssue);
      const nowAcked = { ...issue, acked_at: Date.now(), acked_sev: issue.sev, present: true };
      this.issuesAcked = sortAckedIssues([nowAcked, ...this.issuesAcked]);
    } else {
      this.issuesAcked = this.issuesAcked.filter(isOtherIssue);
      // only back in the open list if it's still happening
      if (issue.present !== false) {
        const reopened = { ...issue, acked_at: null, acked_sev: null };
        this.issues = sortOpenIssues([...this.issues, reopened]);
      }
    }
    this.cdr.markForCheck();

    this.svc.setIssueAck(issue.serial, issue, acked, this.apiBase)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => this.ackDone(key),
        error: (error: unknown) => {
          this.status.emit({
            text: 'Could not save the acknowledgement: ' + requestErrorText(error),
            err: true,
          });
          this.ackDone(key);
        },
      });
  }

  // ------------------------------------------------------------ ack notes
  openNote(issue: FleetIssue): void {
    this.noteEd.open(this.issueKey(issue), issue);
    this.cdr.markForCheck();
  }

  cancelNote(): void {
    this.noteEd.close();
    this.cdr.markForCheck();
  }

  saveNote(issue: FleetIssue): void {
    if (!issue.id || this.noteEd.saving) return;
    this.noteEd.saving = true;
    this.svc.setIssueAck(issue.serial, issue, true, this.apiBase, this.noteEd.payload())
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: () => {
          this.noteEd.close();
          this.cdr.markForCheck();
          this.reload$.next();
        },
        error: (error: unknown) => {
          this.noteEd.saving = false;
          this.noteEd.error = 'Not saved: ' + requestErrorText(error);
          this.cdr.markForCheck();
        },
      });
  }

  // ---------------------------------------------------------------- events
  setEventKind(kind: EventKind | 'all'): void {
    // clicking the active kind again goes back to all
    this.eventKind = this.eventKind === kind ? 'all' : kind;
    this.buildEvents();
    this.cdr.markForCheck();
  }

  eventHasEvidence(event: FleetEvent): boolean {
    return evidenceOf(event.ref) != null;
  }

  /** "Show" on an event: the device page, on the data behind it. */
  showEvent(event: FleetEvent): void {
    if (event.ref) {
      this.openIssue.emit({ serial: event.serial, id: event.ref });
    }
  }

  private buildEvents(): void {
    const allEvents = this.doc?.events ?? [];
    const visibleEvents = allEvents.filter((event) => this.visibleSerials.has(event.serial));
    this.eventKinds = eventKindsOf(visibleEvents);
    // the kind I picked may not exist anymore after a filter change
    if (this.eventKind !== 'all' && !this.eventKinds.includes(this.eventKind)) {
      this.eventKind = 'all';
    }
    const kind = this.eventKind;
    let list = visibleEvents;
    if (kind !== 'all') {
      list = visibleEvents.filter((event) => event.kind === kind);
    }
    this.eventsMatching = list.length;
    this.events = list.slice(0, FLEET_EVENT_ROWS);
  }

  private ackDone(key: string): void {
    const next = new Set(this.ackPending);
    next.delete(key);
    this.ackPending = next;
    this.cdr.markForCheck();
    this.reload$.next();
  }

  licenseBadge(license: FleetLicense): Badge {
    if (license.expired || (license.days_left != null && license.days_left < 0)) {
      return { cls: 'label-danger', text: 'expired' };
    }
    if (license.days_left == null) {
      return { cls: 'label-warning', text: license.status || 'check' };
    }
    let level: Level;
    if (license.kind === 'certificate') {
      level = this.thresholds.cert_days;
    } else {
      level = this.thresholds.license_days;
    }
    const text = `${license.days_left}d left`;
    if (daysLevelOf(license.days_left, level) === 'danger') {
      return { cls: 'label-danger', text };
    }
    return { cls: 'label-warning', text };
  }

  trackBySerial = (_index: number, row: RowVm): string => row.device.serial;
  trackByKpi = (_index: number, kpi: KpiVm): string => kpi.label;
  trackByIssue = (_index: number, issue: FleetIssue): string => this.issueKey(issue);
  trackByLicense = (_index: number, license: FleetLicense): string =>
    license.serial + '|' + (license.kind ?? 'license') + '|' + license.name;
  trackByEvent = (_index: number, event: FleetEvent): string =>
    `${event.serial}|${event.t}|${event.kind}|${event.subject}|${event.title}`;
  trackByKind = (_index: number, kind: string): string => kind;
  trackByGroup = (_index: number, group: GroupVm): string => group.name;
  trackByHygiene = (_index: number, hygiene: FleetPolicyHygiene): string => hygiene.serial;
  trackBySdwan = (_index: number, row: SdwanRowVm): string => row.serial;
  trackByText = (_index: number, text: string): string => text;
  trackByIndex = (index: number): number => index;
  trackByState = (_index: number, segment: StateSegVm): string => segment.state;

  // ---------------------------------------------------------------- loading
  private onDocument(doc: FleetDocument): void {
    if (this.destroyed) return;
    this.doc = doc;
    this.thresholds = thresholdsOf(doc.meta.thresholds);
    this.errorText = '';
    this.loadedOnce = true;
    this.kpis = this.buildKpis(doc);

    this.allRows = [];
    for (const device of doc.devices) {
      this.allRows.push(this.rowVm(device, doc));
    }
    this.rowsTotal = this.allRows.length;

    this.showGroups = !!doc.meta.groups?.length;
    if (!this.showGroups) {
      this.groupFilter = null;
    }
    this.showCompliance = doc.devices.some((device) => !!device.firmware_target);
    this.showSdwan = doc.devices.some((device) => device.sdwan_total > 0);
    this.showHa = doc.devices.some((device) => device.ha_members > 0);
    // older backends don't send these lists at all
    this.ackSupported = Array.isArray(doc.issues_acked);
    this.hygieneSupported = Array.isArray(doc.policy_hygiene);
    this.eventsSupported = Array.isArray(doc.events);
    this.hasCertRows = doc.licenses.some((license) => license.kind === 'certificate');

    // close the note form if its issue isn't acknowledged anymore
    if (this.noteEd.key) {
      const ackedIssues = doc.issues_acked ?? [];
      const stillThere = ackedIssues.some((issue) => this.issueKey(issue) === this.noteEd.key);
      if (!stillThere) {
        this.noteEd.close();
      }
    }

    this.groupsInOrder = this.buildGroups();
    this.groups = this.groupSort.apply(this.groupsInOrder, groupSortValue);
    this.stateSegs = this.buildStateSegs();
    this.applyFilters();
    this.loaded.emit(doc);
    this.status.emit({
      text: `${doc.totals.devices} devices, updated ${new Date().toLocaleTimeString()}`,
      err: false,
    });
  }

  private onError(error: unknown): void {
    if (this.destroyed) return;
    const message = describeApiError(error);
    this.errorText = message;
    this.status.emit({ text: 'Refresh failed: ' + message, err: true });
    this.cdr.markForCheck();
  }

  // ---------------------------------------------------------------- filters
  private visibleSerials = new Set<string>();

  /** Does a row pass all the filters? */
  private rowPassesFilters(row: RowVm, query: string): boolean {
    if (query && !row.haystack.includes(query)) {
      return false;
    }
    if (this.stateFilter === 'attention' && !row.attention) {
      return false;
    }
    if (this.stateFilter === 'offline' && !row.offline) {
      return false;
    }
    if (this.exactState !== null && row.device.state !== this.exactState) {
      return false;
    }
    // a device passes when it is in the group, in a group below it, or
    // (filter '') when it has no group at all
    if (!deviceIsInGroup(groupsOfDevice(row.device), this.groupFilter)) {
      return false;
    }
    return true;
  }

  /** Recomputes everything the filters narrow down, then redraws. */
  private applyFilters(): void {
    const doc = this.doc;
    if (!doc) return;
    const query = this.filterText.trim().toLowerCase();
    const rows = this.allRows.filter((row) => this.rowPassesFilters(row, query));
    this.rowsInOrder = rows;

    const visible = new Set<string>();
    for (const row of rows) {
      visible.add(row.device.serial);
    }
    this.visibleSerials = visible;
    // without a filter I can use the backend totals (they count the issues
    // that didn't fit in the list too)
    const showingAll = !this.filtered;

    this.issues = doc.issues.filter((issue) => visible.has(issue.serial));
    this.issuesTotal = showingAll ? doc.issues_total : this.issues.length;

    const acked = doc.issues_acked ?? [];
    this.issuesAcked = acked.filter((issue) => visible.has(issue.serial));
    if (showingAll) {
      this.issuesAckedTotal = doc.issues_acked_total ?? acked.length;
    } else {
      this.issuesAckedTotal = this.issuesAcked.length;
    }

    this.licensesInOrder = doc.licenses.filter((license) => visible.has(license.serial));
    const hygiene = doc.policy_hygiene ?? [];
    this.hygieneInOrder = hygiene.filter((item) => visible.has(item.serial));
    this.buildSdwan();
    this.updateSortedTables();
    this.buildEvents();
    this.filtersChanged.emit({
      groupFilter: this.groupFilter,
      filtered: this.filtered,
      shown: rows.length,
    });
    this.cdr.markForCheck();
    // render now, so the chart containers exist before the charts look
    // for them
    if (!this.destroyed) {
      this.cdr.detectChanges();
    }
    this.zone.runOutsideAngular(() => setTimeout(() => {
      if (!this.destroyed) {
        this.renderCharts();
      }
    }));
  }

  private buildSdwan(): void {
    const sdwan = this.doc?.sdwan;
    if (!sdwan) {
      this.sdwanMembers = [];
      this.sdwanRowsInOrder = [];
      this.sdwanRows = [];
      return;
    }
    const devices = sdwan.devices.filter((device) => this.visibleSerials.has(device.serial));
    // only the members that at least one visible device has
    this.sdwanMembers = sdwan.members.filter((member) =>
      devices.some((device) => device.cells[member]));

    const metric = this.sdwanMetric;
    let level: Level;
    if (metric === 'loss') {
      level = this.thresholds.sdwan_loss;
    } else if (metric === 'latency') {
      level = this.thresholds.sdwan_latency;
    } else {
      level = this.thresholds.sdwan_jitter;
    }
    const unit = metric === 'loss' ? ' %' : ' ms';

    // every member column starts with its worst value
    const memberKeys: string[] = [];
    for (const member of this.sdwanMembers) {
      memberKeys.push(this.sdwanKey(member));
    }
    this.sdwanSort.descendingFirst = memberKeys;

    this.sdwanRowsInOrder = [];
    for (const device of devices) {
      const cells: SdwanCellVm[] = [];
      const sortValues: Record<string, number | null> = {};
      for (const member of this.sdwanMembers) {
        const cell = device.cells[member];
        cells.push(this.sdwanCell(member, cell, metric, level, unit));
        sortValues[this.sdwanKey(member)] = this.sdwanCellSortValue(cell, metric);
      }
      this.sdwanRowsInOrder.push({
        serial: device.serial, device: device.device, cells, sortValues,
      });
    }
    this.sdwanRows = this.sdwanSort.apply(this.sdwanRowsInOrder, sdwanRowSortValue);
  }

  /** What a cell of the SD-WAN grid sorts on: the value of the metric
   *  shown. A member down is worse than any value, so it gets Infinity
   *  (bigger than every number). A member the device doesn't have gets
   *  null and goes to the bottom. */
  private sdwanCellSortValue(cell: FleetSdwanCell | undefined,
                             metric: SdwanMetric): number | null {
    if (!cell) return null;
    const status = (cell.status || '').toLowerCase();
    if (status && status !== 'up') return Infinity;
    return cell[metric];
  }

  /** One cell of the SD-WAN grid (a device x a member). */
  private sdwanCell(member: string, cell: FleetSdwanCell | undefined,
                    metric: SdwanMetric, level: Level, unit: string): SdwanCellVm {
    if (!cell) {
      return { text: '', cls: 'hm-none', title: `${member}: not configured` };
    }
    const status = (cell.status || '').toLowerCase();
    const value = cell[metric];
    const lossText = cell.loss == null ? '-' : cell.loss.toFixed(1) + ' %';
    const detail = `latency ${fmtMs(cell.latency)}, jitter ${fmtMs(cell.jitter)}, `
      + `loss ${lossText}`;
    if (status && status !== 'up') {
      return { text: status, cls: 'hm-danger', title: `${member} is ${status}; ${detail}` };
    }

    // loss only has a warning level, and it's "more than", not "at least"
    let verdict: Sev | null;
    if (metric === 'loss') {
      verdict = value != null && value > level.warning ? 'warning' : null;
    } else {
      verdict = levelOf(value, level);
    }

    let text = '-';
    if (value != null) {
      text = value.toFixed(value < 10 ? 1 : 0) + unit;
    }
    let cls = 'hm-ok';
    if (verdict === 'danger') {
      cls = 'hm-danger';
    } else if (verdict === 'warning') {
      cls = 'hm-warning';
    }
    return { text, cls, title: `${member}: ${detail}` };
  }

  // ------------------------------------------------------------ view models
  private buildKpis(doc: FleetDocument): KpiVm[] {
    const totals = doc.totals;

    let issuesBadge: Badge;
    if (totals.issues.danger) {
      issuesBadge = { cls: 'label-danger', text: `${totals.issues.danger} to fix` };
    } else if (totals.issues.warning) {
      issuesBadge = { cls: 'label-warning', text: `${totals.issues.warning} to check` };
    } else {
      issuesBadge = { cls: 'label-success', text: 'nothing urgent' };
    }
    const extraParts: string[] = [];
    if (totals.issues.info) {
      extraParts.push(`+ ${totals.issues.info} notes`);
    }
    if (totals.issues_acked) {
      extraParts.push(`${totals.issues_acked} acknowledged`);
    }
    const extras = extraParts.join(', ');

    // With firmware targets I show the compliance, otherwise how many
    // versions run in the fleet
    const targeted = totals.firmware_targeted ?? 0;
    let firmware: KpiVm;
    if (targeted) {
      let badge: Badge;
      if (totals.firmware_behind) {
        badge = { cls: 'label-warning', text: `${totals.firmware_behind} behind target` };
      } else if ((totals.firmware_on_target ?? 0) === targeted) {
        badge = { cls: 'label-success', text: 'all on target' };
      } else {
        badge = { cls: 'label-info', text: 'some ahead of target' };
      }
      firmware = {
        label: 'Firmware',
        value: String(totals.firmware_on_target ?? 0),
        suffix: `/ ${targeted} on target`,
        badge,
      };
    } else {
      let badge: Badge;
      if (totals.upgrades) {
        badge = { cls: 'label-info', text: `${totals.upgrades} can upgrade` };
      } else {
        badge = { cls: 'label-success', text: 'all up to date' };
      }
      firmware = {
        label: 'Firmware',
        value: String(totals.versions.length),
        suffix: totals.versions.length === 1 ? 'version' : 'versions',
        badge,
      };
    }

    const licenseWarningDays = this.thresholds.license_days.warning;
    let licensesBadge: Badge;
    if (totals.licenses_expired) {
      licensesBadge = { cls: 'label-danger', text: `${totals.licenses_expired} expired` };
    } else if (totals.licenses_expiring) {
      licensesBadge = { cls: 'label-warning',
                        text: `${totals.licenses_expiring} within ${licenseWarningDays} d` };
    } else {
      licensesBadge = { cls: 'label-success', text: `none within ${licenseWarningDays} d` };
    }

    let devicesBadge: Badge;
    if (totals.offline) {
      devicesBadge = { cls: 'label-danger', text: `${totals.offline} not reporting` };
    } else {
      devicesBadge = { cls: 'label-success', text: 'all reporting' };
    }

    const kpis: KpiVm[] = [
      {
        label: 'Devices',
        value: String(totals.online),
        suffix: `/ ${totals.devices} reporting`,
        badge: devicesBadge,
      },
      {
        label: 'Throughput',
        value: fmtBps(totals.throughput_bps),
        sub: totals.offline ? 'reporting devices, now' : 'all devices, now',
      },
      {
        label: 'Active sessions',
        value: fmtNum(totals.sessions),
        sub: totals.setup_rate == null ? '' : `${fmtNum(totals.setup_rate)} new per second`,
      },
      {
        label: 'Issues',
        value: String(totals.issues.danger + totals.issues.warning),
        suffix: extras || undefined,
        badge: issuesBadge,
      },
      firmware,
      {
        label: 'Licenses',
        value: String(totals.licenses_expired + totals.licenses_expiring),
        suffix: 'need renewal',
        badge: licensesBadge,
      },
    ];

    const certificatesToRenew = (totals.certificates_expired ?? 0)
      + (totals.certificates_expiring ?? 0);
    if (certificatesToRenew) {
      let badge: Badge;
      if (totals.certificates_expired) {
        badge = { cls: 'label-danger', text: `${totals.certificates_expired} expired` };
      } else {
        badge = { cls: 'label-warning',
                  text: `${totals.certificates_expiring} within ${this.thresholds.cert_days.warning} d` };
      }
      kpis.push({
        label: 'Certificates',
        value: String(certificatesToRenew),
        suffix: 'need renewal',
        badge,
      });
    }

    if (totals.tunnels_total) {
      let badge: Badge;
      if (totals.tunnels_up === totals.tunnels_total) {
        badge = { cls: 'label-success', text: 'all up' };
      } else {
        badge = { cls: 'label-warning', text: `${totals.tunnels_total - totals.tunnels_up} down` };
      }
      kpis.push({
        label: 'IPsec tunnels',
        value: String(totals.tunnels_up),
        suffix: `/ ${totals.tunnels_total} up`,
        badge,
      });
    }

    // policies_unused is missing on older backends
    if (totals.policies_unused != null && (totals.policies_unused || totals.policies_oversize)) {
      kpis.push({
        label: 'Policy cleanup',
        value: String(totals.policies_unused),
        suffix: 'unused',
        sub: totals.policies_oversize ? `${totals.policies_oversize} oversize` : 'across devices',
      });
    }

    if (totals.ssl_users != null) {
      kpis.push({ label: 'SSL-VPN users', value: fmtNum(totals.ssl_users),
                  sub: 'connected across devices' });
    }
    return kpis;
  }

  /** Worst state first, like the default table order. States with no
   *  device are left out. */
  private buildStateSegs(): StateSegVm[] {
    const deviceCount = this.allRows.length;
    if (!deviceCount) return [];

    const counts = new Map<DeviceState, number>();
    for (const row of this.allRows) {
      const state = row.device.state;
      counts.set(state, (counts.get(state) ?? 0) + 1);
    }

    const states = Object.keys(STATE_RANK) as DeviceState[];
    states.sort((first, second) => STATE_RANK[first] - STATE_RANK[second]);

    const segments: StateSegVm[] = [];
    for (const state of states) {
      const count = counts.get(state);
      if (!count) continue;
      segments.push({
        state,
        label: STATE_BADGE[state].text,
        count,
        pct: (count / deviceCount) * 100,
      });
    }
    return segments;
  }

  private buildGroups(): GroupVm[] {
    if (!this.showGroups) return [];
    const groupsByName = new Map<string, GroupVm>();
    for (const row of this.allRows) {
      const device = row.device;

      // A device counts in each of its groups AND in the groups above them
      // ("France/Paris" also counts in "France"), each one once. A device
      // without any group counts in the '' row ("No group").
      let groupNames = groupsWithParents(groupsOfDevice(device));
      if (groupNames.length === 0) {
        groupNames = [''];
      }

      for (const name of groupNames) {
        let group = groupsByName.get(name);
        if (!group) {
          group = {
            name,
            label: name === '' ? 'No group' : groupLeafName(name),
            pathText: name === '' ? 'No group' : groupPathText(name),
            depth: name === '' ? 0 : groupDepth(name),
            devices: 0, reporting: 0, attention: 0,
            throughput: null, sessions: null, issues: 0,
          };
          groupsByName.set(name, group);
        }
        this.addDeviceToGroup(group, row);
      }
    }

    // each group right before the groups inside it, "No group" last
    const groups = [...groupsByName.values()];
    groups.sort((first, second) => compareGroupsAsTree(first.name, second.name));
    return groups;
  }

  /** Adds the figures of one device to the totals of one group. */
  private addDeviceToGroup(group: GroupVm, row: RowVm): void {
    const device = row.device;
    group.devices++;
    // traffic only counts for devices still reporting
    if (!row.offline) {
      group.reporting++;
      if (device.throughput_bps != null) {
        group.throughput = (group.throughput ?? 0) + device.throughput_bps;
      }
      if (device.sessions != null) {
        group.sessions = (group.sessions ?? 0) + device.sessions;
      }
    }
    if (row.attention) {
      group.attention++;
    }
    group.issues += device.issues.danger + device.issues.warning;
  }

  private rowVm(device: FleetDevice, doc: FleetDocument): RowVm {
    const thresholds = this.thresholds;
    const intervalText = device.interval ? `, polled every ${fmtAge(device.interval)}` : '';

    let ha: Badge | null = null;
    if (device.ha_role === 'primary') {
      ha = { cls: 'label-info', text: 'primary' };
    } else if (device.ha_role === 'secondary') {
      ha = { cls: 'label-success', text: 'secondary' };
    } else if (device.ha_role === 'alone') {
      ha = { cls: 'label-danger', text: 'peer missing' };
    }

    const licenseDays = device.license_min_days;
    const licenseLevel = daysLevelOf(licenseDays, thresholds.license_days);

    const acked = device.issues_acked ?? 0;
    // an older backend only sends the total: I treat those as notes
    const ackedSev = device.issues_acked_sev ?? { danger: 0, warning: 0, info: acked };
    const remaining = acked + device.issues.info;
    // what is left on a healthy device: acknowledged issues plus open notes
    const remainingSev: Record<Sev, number> = {
      danger: ackedSev.danger,
      warning: ackedSev.warning,
      info: ackedSev.info + device.issues.info,
    };

    // "1 critical, 2 note"
    const ackedParts: string[] = [];
    for (const sev of ['danger', 'warning', 'info'] as Sev[]) {
      if (ackedSev[sev]) {
        ackedParts.push(`${ackedSev[sev]} ${SEV_WORD[sev]}`);
      }
    }
    const remainingParts: string[] = [];
    if (ackedParts.length) {
      remainingParts.push(ackedParts.join(', ') + ' acknowledged');
    }
    if (device.issues.info) {
      const noteWord = device.issues.info === 1 ? 'note' : 'notes';
      remainingParts.push(`${device.issues.info} open ${noteWord}`);
    }
    const remainingText = remainingParts.join('; ');

    const offline = device.state === 'offline' || device.state === 'unreachable';

    const target = device.firmware_target;
    let firmware: Badge | null = null;
    if (device.firmware_status === 'on-target') {
      firmware = { cls: 'label-success', text: 'on target' };
    } else if (device.firmware_status === 'behind') {
      firmware = { cls: 'label-warning', text: 'behind' };
    } else if (device.firmware_status === 'ahead') {
      firmware = { cls: 'label-info', text: 'ahead' };
    }

    // throughput trend for the sparkline
    const series = doc.throughput.find((item) => item.serial === device.serial);
    const numbers: number[] = [];
    for (const value of series?.values ?? []) {
      if (value != null) {
        numbers.push(value);
      }
    }

    // text the search box matches against
    const searchParts = [device.label, device.serial, device.host, device.hostname,
                         groupsOfDevice(device).join(' '), device.version,
                         STATE_BADGE[device.state]?.text];
    const haystack = searchParts.filter(Boolean).join(' ').toLowerCase();

    let stateMore: RowVm['stateMore'] = null;
    if (device.state === 'ok' && remaining) {
      stateMore = {
        text: `${remaining} ${remaining === 1 ? 'issue' : 'issues'}`,
        title: `Nothing needs attention; still listed: ${remainingText}`,
        cls: worstSevClass(remainingSev),
      };
    }

    let ageTitle = 'No poll recorded';
    if (device.last_poll) {
      ageTitle = fmtStamp(device.last_poll) + intervalText;
      if (device.poll_ok_pct != null) {
        ageTitle += `; ${device.poll_ok_pct}% of recent calls answered`;
      }
    }

    let pollCls = 'dim';
    if (offline) {
      pollCls = 'err-warn';
    } else if (device.poll_ok_pct != null && device.poll_ok_pct < 100) {
      pollCls = 'util-warn';
    }

    let sparkTitle = '';
    if (numbers.length) {
      sparkTitle = `Throughput over the window: min ${fmtBps(Math.min(...numbers))}, `
        + `max ${fmtBps(Math.max(...numbers))}`;
    }

    // CPU and memory are only judged when they look like percentages
    let cpuCls = '';
    if (isPct(device.cpu) || device.cpu == null) {
      cpuCls = levelCls(device.cpu, thresholds.cpu);
    }
    let memCls = '';
    if (isPct(device.mem) || device.mem == null) {
      memCls = levelCls(device.mem, thresholds.memory);
    }

    let ifacesCls = '';
    if (!device.ifaces_total) {
      ifacesCls = 'dim';
    } else if (device.ifaces_up! < device.ifaces_total) {
      ifacesCls = 'util-warn';
    }
    let tunnelsCls = '';
    if (!device.tunnels_total) {
      tunnelsCls = 'dim';
    } else if (device.tunnels_up < device.tunnels_total) {
      tunnelsCls = 'util-warn';
    }
    let sdwanCls = '';
    if (!device.sdwan_total) {
      sdwanCls = 'dim';
    } else if (device.sdwan_up < device.sdwan_total) {
      sdwanCls = 'err-warn';
    }

    let license = '-';
    let licenseCls = 'dim';
    if (licenseDays != null) {
      license = licenseDays < 0 ? 'expired' : `${licenseDays} d`;
      if (licenseLevel === 'danger') {
        licenseCls = 'err-warn';
      } else if (licenseLevel === 'warning') {
        licenseCls = 'util-warn';
      } else {
        licenseCls = '';
      }
    }

    const groupTexts: string[] = [];
    for (const group of groupsOfDevice(device)) {
      groupTexts.push(groupPathText(group));
    }

    return {
      device,
      haystack,
      groupTexts,
      attention: device.state !== 'ok',
      offline,
      state: STATE_BADGE[device.state] ?? { cls: 'label-info', text: device.state },
      stateMore,
      acked,
      ackedCls: worstSevClass(ackedSev),
      ackedTitle: ackedParts.length ? ackedParts.join(', ') + ' acknowledged'
        : `${acked} acknowledged`,
      firmware,
      firmwareTitle: target ? `Target FortiOS ${target}` : '',
      age: fmtAge(device.poll_age),
      ageTitle,
      pollCls,
      throughput: fmtBps(device.throughput_bps),
      spark: series ? sparkPath(series.values, SPARK_W, SPARK_H) : '',
      sparkTitle,
      sparkPeak: numbers.length ? Math.max(...numbers) : null,
      sessions: fmtNum(device.sessions),
      cpu: fmtPctOrNum(device.cpu),
      cpuCls,
      mem: fmtPctOrNum(device.mem),
      memCls,
      ifaces: device.ifaces_total ? `${device.ifaces_up} / ${device.ifaces_total}` : '-',
      ifacesCls,
      tunnels: device.tunnels_total ? `${device.tunnels_up} / ${device.tunnels_total}` : '-',
      tunnelsCls,
      sdwan: device.sdwan_total ? `${device.sdwan_up} / ${device.sdwan_total}` : '-',
      sdwanCls,
      ha,
      license,
      licenseCls,
      licenseTitle: device.license_min_name ? `Soonest: ${device.license_min_name}` : '',
    };
  }

  // ------------------------------------------------------------------ charts
  private renderCharts(): void {
    const doc = this.doc;
    if (!doc) return;
    const visible = this.visibleSerials;
    const onlyVisible = (list: FleetSeries[]) =>
      list.filter((series) => visible.has(series.serial));
    const throughput = onlyVisible(doc.throughput);
    const sessions = onlyVisible(doc.sessions);
    const cpu = onlyVisible(doc.cpu);
    const memory = onlyVisible(doc.memory);

    const timeBases: number[][] = [];
    for (const series of [...throughput, ...sessions, ...cpu, ...memory]) {
      timeBases.push(series.t);
    }
    this.kit.beginPass(timeBases);

    // One colour per device on every chart, the same whatever the filters.
    const colorOf: Record<string, string> = {};
    doc.meta.serials.forEach((serial, index) => {
      colorOf[serial] = this.kit.pal[index % this.kit.pal.length];
    });

    const notes: Record<string, string> = {};
    notes['throughput'] = this.devicesLine('fl-throughput', throughput, fmtBps, colorOf,
      'Needs at least two interface polls per device.');
    notes['sessions'] = this.devicesLine('fl-sessions', sessions, fmtNum, colorOf,
      'No session-list history in this window.');
    notes['cpu'] = this.devicesLine('fl-cpu', cpu, fmtPct, colorOf,
      'No CPU history in this window.', this.thresholds.cpu);
    notes['mem'] = this.devicesLine('fl-mem', memory, fmtPct, colorOf,
      'No memory history in this window.', this.thresholds.memory);
    this.renderDaily(doc, colorOf);
    this.renderVersions(doc);
    this.kit.endPass();

    // only go back into Angular when the notes really changed
    if (JSON.stringify(notes) !== JSON.stringify(this.chartNotes)) {
      this.zone.run(() => {
        this.chartNotes = notes;
        this.cdr.markForCheck();
      });
    }
  }

  /** One line per device, only the CHART_MAX_DEVICES busiest ones. Returns
   *  the note for the card header ('' when every device is drawn).
   *  `pctLevel` means a percentage chart, and draws its warning and
   *  critical levels when the axis really is 0..100. */
  private devicesLine(id: string, all: FleetSeries[], formatter: Formatter,
                      colorOf: Record<string, string>, emptyMessage: string,
                      pctLevel: Level | null = null): string {
    const isPercentChart = pctLevel != null;
    if (!all.length) {
      this.kit.markEmpty(id, this.filtered ? 'No matching device has data here.' : emptyMessage);
      return '';
    }

    const peakOf = (series: FleetSeries): number => {
      const values = series.max ?? series.values;
      const numbers = values.filter((value): value is number => value != null);
      return Math.max(-Infinity, ...numbers);
    };

    let list = all;
    if (all.length > CHART_MAX_DEVICES) {
      list = all.slice();
      list.sort((first, second) => peakOf(second) - peakOf(first));
      list = list.slice(0, CHART_MAX_DEVICES);
    }

    // I only fix a percentage axis to 0..100 when every value fits in it
    const inRange = (values: Values | undefined): boolean => {
      if (!values) return true;
      return values.every((value) => value == null || (value >= 0 && value <= 100));
    };
    const asPct = isPercentChart && list.every((series) =>
      inRange(series.values) && inRange(series.min) && inRange(series.max));
    const axisFormatter = isPercentChart && !asPct ? fmtNum : formatter;

    const chartSeries: SeriesOption[] = [];
    list.forEach((series, index) => {
      // the threshold lines go on the first series only
      let markLine = undefined;
      if (index === 0 && asPct && pctLevel) {
        markLine = this.kit.thresholdMarks(pctLevel, fmtPct);
      }
      chartSeries.push(...this.kit.lineWithBand(series.name, series.t, series.values, series, {
        color: colorOf[series.serial],
        markLine,
      }));
    });

    const yAxis = this.kit.valAxis(axisFormatter);
    if (asPct) {
      Object.assign(yAxis, { min: 0, max: 100 });
    }
    this.kit.setChart(id, this.kit.base({
      xAxis: this.kit.timeAxis(),
      yAxis,
      tooltip: this.kit.bandTooltip(axisFormatter),
      series: chartSeries,
      ...this.kit.timeZoom(),
    }), { linked: true });

    if (list.length < all.length) {
      return `busiest ${list.length} of ${all.length} devices, by peak`;
    }
    return '';
  }

  private renderDaily(doc: FleetDocument, colorOf: Record<string, string>): void {
    const daily = doc.daily;
    const series = daily.series.filter((item) => this.visibleSerials.has(item.serial));
    if (!daily.days.length || !series.length) {
      let message = 'Needs at least two interface polls per device.';
      if (this.filtered && daily.series.length) {
        message = 'No matching device has data here.';
      }
      this.kit.markEmpty('fl-daily', message);
      return;
    }

    const bars: SeriesOption[] = [];
    for (const item of series) {
      bars.push({
        name: item.name, type: 'bar', stack: 'days', barMaxWidth: 40,
        color: colorOf[item.serial], data: item.values,
      } as SeriesOption);
    }

    this.kit.setChart('fl-daily', this.kit.base({
      grid: { left: 12, right: 20, top: 25, bottom: 55, containLabel: true },
      xAxis: {
        type: 'category', data: daily.days,
        axisLine: { lineStyle: { color: this.kit.axisLineColor() } },
        axisLabel: { color: this.kit.cssVar('--clr-ink-dim') },
      },
      yAxis: this.kit.valAxis(fmtBytes),
      tooltip: { axisPointer: { type: 'shadow' }, valueFormatter: fmtBytes as never },
      series: bars,
    }));
  }

  private renderVersions(doc: FleetDocument): void {
    // how many visible devices run each version
    const counts = new Map<string, number>();
    for (const row of this.rows) {
      const version = row.device.version || 'unknown';
      counts.set(version, (counts.get(version) ?? 0) + 1);
    }

    // backend order (oldest first), limited to the visible devices
    const versions: { name: string; value: number }[] = [];
    for (const item of doc.totals.versions) {
      if (counts.has(item.name)) {
        versions.push({ name: item.name, value: counts.get(item.name)! });
      }
    }
    if (!versions.length) {
      this.kit.markEmpty('fl-versions', this.filtered
        ? 'No matching device.' : 'No version reported yet.');
      return;
    }

    const known = versions.filter((item) => item.name !== 'unknown');
    const newest = known.length ? known[known.length - 1].name : null;

    const categories: string[] = [];
    const bars: { value: number; itemStyle: { color: string } }[] = [];
    for (const item of versions) {
      categories.push(item.name === 'unknown' ? item.name : 'FortiOS ' + item.name);
      // the newest version in use stands out, older ones are muted
      const color = item.name === newest ? this.kit.pal[0] : this.kit.pal[6];
      bars.push({ value: item.value, itemStyle: { color } });
    }

    const deviceCount = (count: number) => `${count} device${count === 1 ? '' : 's'}`;

    this.kit.setChart('fl-versions', this.kit.base({
      grid: { left: 12, right: 30, top: 10, bottom: 10, containLabel: true },
      legend: { show: false },
      tooltip: {
        axisPointer: { type: 'shadow' },
        valueFormatter: deviceCount as never,
      },
      xAxis: Object.assign(this.kit.valAxis(), { minInterval: 1 }),
      yAxis: {
        type: 'category',
        data: categories,
        axisLabel: { color: this.kit.cssVar('--clr-ink') },
        axisLine: { lineStyle: { color: this.kit.axisLineColor() } },
      },
      series: [{
        type: 'bar', name: 'Devices', barMaxWidth: 26,
        label: { show: true, position: 'right',
                 color: this.kit.cssVar('--clr-ink-dim') },
        data: bars,
      }] as SeriesOption[],
    } as EChartsOption));
  }
}

function fmtMs(value: number | null | undefined): string {
  if (value == null) return '-';
  return value.toFixed(value < 10 ? 1 : 0) + ' ms';
}

/** The text of a failed request: the API's message when it sent one. */
function requestErrorText(error: unknown): string {
  return describeApiError(error);
}

// Re-exported for code that imported these from the fleet file before the
// helpers moved to fortinet-shared.ts.
export { ackNote, sortAckedIssues, sortOpenIssues } from './fortinet-shared';
export type { AckFields, DeviceRef } from './fortinet-shared';
